import type { SpreadsheetDocument, SpreadsheetGridInput } from '../models/SpreadsheetDocument';
import type { ZoneNvrSheet } from '../map-editor-features/nvr-template/lib/nvrTemplate';
import type { SpreadsheetBatchExportResult } from '../components/spreadsheet-docs/exportSpreadsheet';
import { NVR_IP_COLUMN, NVR_CH_NAME_COLUMN } from './NvrTemplateSpreadsheetService';
import {
  FOCUSAI_HEADER,
  focusAiCsvText,
} from '../map-editor-features/nvr-template/lib/focusAiCsv';

/**
 * NVR 템플릿 문서 ↔ NVR .xls 변환.
 *
 * .xls 는 맵에디터의 nvr-template 라이브러리(writeBiff8Xls, xlwt 바이트 동일)로 만든다 —
 * SheetJS 로 쓴 .xls 는 셀 내용이 같아도 NVR 이 거부한다. PASSWORD 는 문자열 "1234",
 * RTSP 4열은 셀 없음, CH NUM 은 행 순번. 이 규칙은 buildNvrRows 가 담당한다.
 */

const IP_HEADER = /^ip$/i;
const CH_NAME_HEADER = /^ch\s*_?\s*name$/i;

function findColumn(names: string[], re: RegExp): number {
  return names.findIndex((n) => re.test(n.trim()));
}

/** 문서 → 구역 시트. IP 가 비어 있는 행은 건너뛴다(NVR 이 빈 IP 채널을 거부). */
export function documentToZoneSheet(doc: SpreadsheetDocument): ZoneNvrSheet {
  const names = doc.columns.map((c) => c.name);
  const ipIdx = findColumn(names, IP_HEADER);
  const nameIdx = findColumn(names, CH_NAME_HEADER);
  if (ipIdx < 0 || nameIdx < 0) {
    throw new Error(
      `'${doc.name}' 문서에 ${NVR_IP_COLUMN} / ${NVR_CH_NAME_COLUMN} 컬럼이 필요합니다.`,
    );
  }
  const ipCol = doc.columns[ipIdx].id;
  const nameCol = doc.columns[nameIdx].id;
  const channels = doc.rows
    .map((r) => ({ ip: (r.cells[ipCol] ?? '').trim(), chName: (r.cells[nameCol] ?? '').trim() }))
    .filter((ch) => ch.ip !== '');
  return { zoneNumber: doc.name, zoneName: doc.name, channels };
}

/**
 * 업로드한 엑셀에서 IP / CH NAME 두 열만 남긴다. NVR 템플릿 원본(11열)이든 두 열만 있는
 * 표든 헤더 이름으로 찾는다. 못 찾으면 거부 메시지를 돌려 잘못된 파일이 문서가 되는 것을 막는다.
 */
export function pickNvrColumns(grid: SpreadsheetGridInput): SpreadsheetGridInput | string {
  if (isFocusAiHeaders(grid.headers)) return pickFocusAiColumns(grid);
  const ipIdx = findColumn(grid.headers, IP_HEADER);
  const nameIdx = findColumn(grid.headers, CH_NAME_HEADER);
  if (ipIdx < 0 || nameIdx < 0) {
    return `엑셀 1행에 ${NVR_IP_COLUMN} 와 ${NVR_CH_NAME_COLUMN} 헤더가 있어야 합니다.`;
  }
  return {
    headers: [NVR_IP_COLUMN, NVR_CH_NAME_COLUMN],
    dataRows: grid.dataRows
      .map((row) => [row[ipIdx] ?? '', row[nameIdx] ?? ''])
      .filter(([ip, name]) => ip.trim() !== '' || name.trim() !== ''),
  };
}

/**
 * 포커스AI NVR [CSV 등록] 형식(헤더 protocol,ip,onvifAddress,…,title)인지 — 대소문자·공백 무시,
 * 열 순서는 상관없다. 이 형식으로 올린 문서만 NVR 원격 등록을 할 수 있다.
 */
export function isFocusAiHeaders(headers: ReadonlyArray<string>): boolean {
  const names = new Set(headers.map((h) => h.trim().toLowerCase()));
  return FOCUSAI_HEADER.every((h) => names.has(h.toLowerCase()));
}

export function isFocusAiDocument(doc: SpreadsheetDocument): boolean {
  return isFocusAiHeaders(doc.columns.map((c) => c.name));
}

/** 포커스AI 업로드는 10열을 표준 순서로 그대로 둔다(원격 등록·CSV 내보내기에 다 필요). */
function pickFocusAiColumns(grid: SpreadsheetGridInput): SpreadsheetGridInput {
  const idx = FOCUSAI_HEADER.map((h) =>
    grid.headers.findIndex((g) => g.trim().toLowerCase() === h.toLowerCase()),
  );
  return {
    headers: [...FOCUSAI_HEADER],
    dataRows: grid.dataRows
      .map((row) => idx.map((i) => (row[i] ?? '').trim()))
      .filter((row) => row.some((v) => v !== '')),
  };
}

/** 원격 등록에 보내는 카메라 한 대 — 백엔드 NvrCamera 와 같은 모양. */
export interface FocusAiCamera {
  channel: number;
  ip: string;
  title: string;
  id: string;
  password: string;
  onvifRtspPort: number;
}

/** 포커스AI 문서의 행 값 — 헤더 이름 → 셀 값. */
function focusAiRows(doc: SpreadsheetDocument): Record<string, string>[] {
  const colId = new Map(doc.columns.map((c) => [c.name.trim().toLowerCase(), c.id]));
  return doc.rows.map((r) =>
    Object.fromEntries(
      FOCUSAI_HEADER.map((h) => [h, (r.cells[colId.get(h.toLowerCase()) ?? ''] ?? '').trim()]),
    ),
  );
}

/**
 * 포커스AI 문서 → 원격 등록 카메라 목록. ip 가 빈 행은 건너뛰고, channel·포트가 정수가 아니면
 * 몇 번째 행인지 알려 준다(나머지 검사는 서버가 한다).
 */
export function documentToFocusAiCameras(doc: SpreadsheetDocument): FocusAiCamera[] {
  if (!isFocusAiDocument(doc)) {
    throw new Error(`'${doc.name}' 은(는) 포커스AI CSV 형식 문서가 아닙니다.`);
  }
  const cams: FocusAiCamera[] = [];
  focusAiRows(doc).forEach((r, i) => {
    if (r.ip === '') return;
    const row = i + 1;
    const channel = Number(r.channel);
    if (!Number.isInteger(channel) || channel < 1) {
      throw new Error(`${row}행 channel 이 1 이상 정수가 아닙니다: '${r.channel}'`);
    }
    const port = r.onvifRtspPort === '' ? 554 : Number(r.onvifRtspPort);
    if (!Number.isInteger(port)) {
      throw new Error(`${row}행 onvifRtspPort 가 정수가 아닙니다: '${r.onvifRtspPort}'`);
    }
    cams.push({ channel, ip: r.ip, title: r.title, id: r.id, password: r.password, onvifRtspPort: port });
  });
  if (cams.length === 0) throw new Error(`'${doc.name}' 문서에 ip 가 있는 행이 없습니다.`);
  return cams;
}

/** 포커스AI 문서를 녹화기 [CSV 등록] 파일 그대로(10열, CRLF) 만든다. */
function focusAiDocumentCsv(doc: SpreadsheetDocument): Uint8Array {
  const rows = focusAiRows(doc)
    .filter((r) => r.ip !== '')
    .map((r) => FOCUSAI_HEADER.map((h) => r[h]));
  if (rows.length === 0) throw new Error(`'${doc.name}' 문서에 ip 가 있는 행이 없습니다.`);
  return new TextEncoder().encode(focusAiCsvText([[...FOCUSAI_HEADER], ...rows]));
}

/** 문서 1개를 내려받는다 — 기존 NVR 은 .xls, 포커스AI 문서는 업로드 형식 그대로 .csv. */
export async function exportNvrTemplateXls(doc: SpreadsheetDocument): Promise<void> {
  if (isFocusAiDocument(doc)) {
    const { downloadBlob } = await import('../map-editor-features/nvr-template/lib/nvrTemplate');
    downloadBlob(new Blob([focusAiDocumentCsv(doc)], { type: 'text/csv' }), `${nvrFileBase(doc.name)}.csv`);
    return;
  }
  const sheet = requireNvrChannels(doc);
  const { buildNvrXlsBytes, downloadBlob } = await import(
    '../map-editor-features/nvr-template/lib/nvrTemplate'
  );
  const bytes = buildNvrXlsBytes(sheet);
  downloadBlob(
    new Blob([bytes], { type: 'application/vnd.ms-excel' }),
    `${nvrFileBase(doc.name)}.xls`,
  );
}

function requireNvrChannels(doc: SpreadsheetDocument): ZoneNvrSheet {
  const sheet = documentToZoneSheet(doc);
  if (sheet.channels.length === 0) {
    throw new Error(`'${doc.name}' 문서에 IP 가 있는 행이 없습니다.`);
  }
  return sheet;
}

function nvrFileBase(name: string): string {
  return (
    name
      .normalize('NFC')
      .replace(/[\\/:*?"<>|]+/g, '_')
      .trim()
      .replace(/[. ]+$/g, '') || 'nvr_template'
  );
}

/** 현재 문서들을 각각 NVR .xls(포커스AI 문서는 .csv)로 만들어 ZIP 한 개로 내려받고, 제외된 문서를 보고한다. */
export async function exportNvrTemplatesZip(
  documents: SpreadsheetDocument[],
): Promise<SpreadsheetBatchExportResult> {
  const [{ buildNvrXlsBytes, downloadBlob }, { createZip }] = await Promise.all([
    import('../map-editor-features/nvr-template/lib/nvrTemplate'),
    import('../map-editor-features/nvr-template/lib/zip'),
  ]);
  const files: { name: string; data: Uint8Array }[] = [];
  const issues: SpreadsheetBatchExportResult['issues'] = [];
  const usedNames = new Set<string>();
  for (const doc of documents) {
    try {
      const focusAi = isFocusAiDocument(doc);
      const data = focusAi ? focusAiDocumentCsv(doc) : buildNvrXlsBytes(requireNvrChannels(doc));
      const ext = focusAi ? 'csv' : 'xls';
      const base = nvrFileBase(doc.name);
      let name = `${base}.${ext}`;
      let suffix = 2;
      // 경로 문자를 치환한 이름과 대소문자만 다른 이름도 압축 해제 시 덮어쓰지 않는다.
      while (usedNames.has(name.toLowerCase())) name = `${base} (${suffix++}).${ext}`;
      usedNames.add(name.toLowerCase());
      files.push({ name, data });
    } catch (error) {
      issues.push({
        name: doc.name,
        message: error instanceof Error ? error.message : '문서 내보내기 실패',
      });
    }
  }
  if (files.length) {
    downloadBlob(new Blob([createZip(files)], { type: 'application/zip' }), 'nvr_templates.zip');
  }
  return { fileCount: files.length, issues };
}
